import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Button, Tabs, Tab, Box, TextField } from "@mui/material";
import EditText from "../../components/editText";
import ManageFaq from "../../features/faq/ManageFaq";
import Loader from "../../constants/Loader";
import { errorToast, successToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody, Permission } from "../../types/General";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useLazyGetCmsQuery, useUpdateCmsMutation } from "../../service/cms";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import useAuth from "../../hooks/useAuth";
import { USER_ROLES } from "../../utils/enums";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageCms = () => {
  const userData = useAuth();
  const [hidePermission, setHidePermission] = useState<Permission>();

  const navigate = useNavigate();
  const [terms, setTerms] = useState(" ");
  const [aboutUs, setAboutUs] = useState(" ");
  const [privacyPolicy, setPrivacyPolicy] = useState(" ");

  const [loading, setLoading] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+91");

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const [getCms] = useLazyGetCmsQuery();
  const [updateCms] = useUpdateCmsMutation();

  const getCmsData = async () => {
    setLoading(true);
    try {
      const response = await getCms().unwrap();
      setLoading(false);

      if (response?.status === 200) {
        formik.setFieldValue("email", response?.data?.email || "");
        formik.setFieldValue("phone", response?.data?.phone_no);

        setAboutUs(response?.data?.about_us || " ");

        setPrivacyPolicy(response?.data?.privacy_policy || " ");

        setTerms(response?.data?.terms_and_conditions || " ");

        setPhoneCode(response?.data?.country_code || "");
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    setPhoneCode(country?.dialCode);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      phone: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Enter a valid email address"
        ),

      phone: Yup.string()
        .required("Phone number is required")
        .min(6, "Phone number must be at least 6 characters")
        .max(16, "Phone number must be at least 16 characters"),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);

      const body = {
        email: formik.values.email,
        phone_no: formik.values.phone,
        country_code: (phoneCode.includes("+") ? "" : "+") + phoneCode,
        terms_and_conditions: terms,

        privacy_policy: privacyPolicy,

        about_us: aboutUs,
      };

      if (!aboutUs?.length) {
        return errorToast("About us is empty");
      }

      if (!terms?.length) {
        return errorToast("Terms and conditions is empty");
      }

      if (!privacyPolicy?.length) {
        return errorToast("Privacy policy is empty");
      }

      try {
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await updateCms({
          body: encryptedBody,
        }).unwrap();
        if (response?.status === 200) {
          successToast("CMS updated successfully!");
        }
      } catch (error: any) {
        errorToast(error?.data?.message || "");
      }
    },
  });

  useEffect(() => {
    getCmsData();
  }, []);

  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Manage CMS"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />

      <div className="main_title">
        <h1>Manage CMS</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          CMS
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Contact Us " {...a11yProps(0)} />
              <Tab label="About Us" {...a11yProps(1)} />
              <Tab label="Privacy Policy" {...a11yProps(2)} />
              <Tab label="Terms And Conditions" {...a11yProps(3)} />
              <Tab label="FAQ" {...a11yProps(4)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label>Email Address</label>
                <TextField
                  className="text_field"
                  hiddenLabel
                  type={"email"}
                  name="email"
                  variant="outlined"
                  fullWidth
                  placeholder="Email"
                  id="email"
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                      return;
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  inputProps={{ maxLength: 40 }}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </div>
              <div className="control_group w_50">
                <label>Phone Number</label>

                <PhoneInput
                  enableSearch={true}
                  value={phoneCode + formik.values.phone}
                  country={"us"}
                  inputClass="phoneInput"
                  inputStyle={{ width: "100%" }}
                  buttonClass="phoneBtn"
                  placeholder="Phone Number"
                  onChange={(phone, country) =>
                    handleChangePhone(phone, country)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <h6 className="err_msg">
                    {formik.touched.phone && formik.errors.phone}
                  </h6>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div className="form_btn end">
              {userData?.user_role_type === USER_ROLES?.ADMIN ||
              hidePermission?.can_add_edit ? (
                <Button className="btn btn_secondary" type="submit">
                  Save
                </Button>
              ) : null}
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={aboutUs} setContent={setAboutUs} />
            {userData?.user_role_type === USER_ROLES?.ADMIN ||
            hidePermission?.can_add_edit ? (
              <div className="form_btn end">
                <Button className="btn btn_secondary" type="submit">
                  Save
                </Button>
              </div>
            ) : null}
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={privacyPolicy} setContent={setPrivacyPolicy} />
            <div className="form_btn end">
              {userData?.user_role_type === USER_ROLES?.ADMIN ||
              hidePermission?.can_add_edit ? (
                <Button className="btn btn_secondary" type="submit">
                  Save
                </Button>
              ) : null}
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={3}>
          <form onSubmit={formik.handleSubmit}>
            <EditText content={terms} setContent={setTerms} />
            <div className="form_btn end">
              {userData?.user_role_type === USER_ROLES?.ADMIN ||
              hidePermission?.can_add_edit ? (
                <Button className="btn btn_secondary" type="submit">
                  Save
                </Button>
              ) : null}
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={4}>
          <ManageFaq hidePermission={hidePermission} />
        </CustomTabPanel>
      </div>
    </MainContainer>
  );
};

export default ManageCms;
